import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { AddChip } from '@/components/ui/add-chip'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { hasFieldErrors } from '@/lib/contacts/errors'
import { ApiError, deleteJSON, getJSON, postJSON } from '@/lib/api'
import type { MemberPortalOrganization, MemberSessionResponse } from './MemberSignIn'
import { memberSessionKey } from './memberPortalSession'

type RosterPlace = { id: number; name: string | null; email: string | null }
function AddPersonForm({
  name,
  email,
  pending,
  onNameChange,
  onEmailChange,
  onAdd,
  onCancel,
}: {
  name: string
  email: string
  pending: boolean
  onNameChange: (value: string) => void
  onEmailChange: (value: string) => void
  onAdd: () => void
  onCancel: () => void
}) {
  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1 space-y-1">
        <Label htmlFor="roster-add-name">Name</Label>
        <Input
          id="roster-add-name"
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Full name"
        />
      </div>
      <div className="flex-1 space-y-1">
        <Label htmlFor="roster-add-email">Email</Label>
        <Input
          id="roster-add-email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          placeholder="name@company.com"
        />
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" onClick={onAdd} disabled={pending}>
          Add
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function RosterRows({
  places,
  onRemove,
}: {
  places: RosterPlace[]
  onRemove: (id: number) => void
}) {
  return (
    <div className="mt-2 divide-y divide-border">
      {places.map((place) => (
        <div key={place.id} className="flex items-center gap-3 py-2">
          <Avatar className="size-8">
            <AvatarFallback>
              {(place.name ?? '?')
                .split(' ')
                .map((part) => part[0])
                .join('')}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-sm">{place.name}</p>
            <p className="truncate text-caption text-muted-foreground">{place.email}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Remove ${place.name} from roster`}
            className="size-8 border-transparent bg-transparent px-0 text-muted-foreground shadow-none"
            onClick={() =>
              window.confirm(`Remove ${place.name} from the roster?`) && onRemove(place.id)
            }
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        </div>
      ))}
    </div>
  )
}

export function RosterPanel({
  organization,
  dashboard = false,
}: {
  organization: MemberPortalOrganization
  dashboard?: boolean
}) {
  const state = useRosterPanel(organization)
  const {
    search,
    setSearch,
    adding,
    setAdding,
    name,
    setName,
    email,
    setEmail,
    add,
    count,
    atCap,
    mutationError,
    forbidden,
  } = state
  if (dashboard && forbidden) return <Navigate to="/portal" replace />
  return (
    <section
      className={
        dashboard ? 'rounded-lg border border-border bg-card px-4 py-3' : 'px-4 py-4 sm:px-6'
      }
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-body font-medium">
          Roster ·{' '}
          {organization.roster_cap === null ? count : `${count} of ${organization.roster_cap}`}
        </h2>
        {atCap ? (
          <span className="text-caption text-muted-foreground">Roster is full</span>
        ) : (
          !adding && <AddChip onClick={() => setAdding(true)}>Add person</AddChip>
        )}
      </div>
      {mutationError && (
        <p className="mt-3 text-caption text-destructive" role="alert">
          {rosterMutationMessage(mutationError)}
        </p>
      )}
      {adding && (
        <AddPersonForm
          name={name}
          email={email}
          pending={add.isPending}
          onNameChange={setName}
          onEmailChange={setEmail}
          onAdd={() => add.mutate()}
          onCancel={() => setAdding(false)}
        />
      )}
      <div className="relative mt-3">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          aria-label="Search roster"
          className="h-9 pl-8"
          placeholder="Search name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <RosterResults state={state} dashboard={dashboard} />
    </section>
  )
}

function rosterMutationMessage(error: Error) {
  if (error instanceof ApiError && hasFieldErrors(error.body)) {
    const messages = Object.entries(error.body.errors).flatMap(([field, errors]) =>
      errors.map((message) =>
        field === 'base'
          ? message
          : `${field.charAt(0).toUpperCase()}${field.slice(1).replaceAll('_', ' ')} ${message}`,
      ),
    )
    if (messages.length) return messages.join('. ')
  }
  return error.message || 'Unable to update roster. Try again.'
}

function useRosterPanel(organization: MemberPortalOrganization) {
  const queryClient = useQueryClient()
  const session = useQuery<MemberSessionResponse>({
    queryKey: memberSessionKey,
    queryFn: () => getJSON('/api/v1/member_portal/session'),
    enabled: false,
  })
  const person = session.data?.person
  const viewer = JSON.stringify([person?.id, person?.name, person?.email])
  const [search, setSearch] = React.useState('')
  const [adding, setAdding] = React.useState(false)
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const rosterQuery = useQuery<{ roster_places: RosterPlace[] }>({
    queryKey: ['member-portal-roster', organization.id, viewer],
    queryFn: () => getJSON(`/api/v1/member_portal/organizations/${organization.id}/roster`),
  })
  const add = useMutation({
    mutationFn: () =>
      postJSON<{ roster_place: RosterPlace }>(
        `/api/v1/member_portal/organizations/${organization.id}/roster`,
        { roster_place: { name, email } },
      ),
    onSuccess: () => {
      setAdding(false)
      setName('')
      setEmail('')
      queryClient.invalidateQueries({ queryKey: ['member-portal-roster', organization.id] })
    },
  })
  const remove = useMutation({
    mutationFn: (id: number) =>
      deleteJSON(`/api/v1/member_portal/organizations/${organization.id}/roster/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['member-portal-roster', organization.id] }),
  })
  const places = (rosterQuery.data?.roster_places ?? []).filter((place) => {
    const term = search.toLowerCase()
    return (
      (place.name ?? '').toLowerCase().includes(term) ||
      (place.email ?? '').toLowerCase().includes(term)
    )
  })
  const count = rosterQuery.data?.roster_places?.length ?? organization.roster_count
  const atCap = organization.roster_cap !== null && count >= organization.roster_cap

  const mutationError = add.error ?? remove.error
  const forbidden = [rosterQuery.error, add.error, remove.error].some(
    (error) => error instanceof ApiError && error.status === 404,
  )
  return {
    search,
    setSearch,
    adding,
    setAdding,
    name,
    setName,
    email,
    setEmail,
    add,
    remove,
    rosterQuery,
    places,
    count,
    atCap,
    mutationError,
    forbidden,
  }
}
function RosterResults({
  state,
  dashboard,
}: {
  state: ReturnType<typeof useRosterPanel>
  dashboard: boolean
}) {
  const { rosterQuery, places, search, remove, count } = state
  return (
    <>
      {' '}
      {rosterQuery.isLoading ? (
        <p className="mt-3 text-caption text-muted-foreground" role="status">
          Loading roster...
        </p>
      ) : rosterQuery.isError ? (
        <p className="mt-3 text-caption text-destructive" role="alert">
          Unable to load roster.
        </p>
      ) : (
        <>
          {dashboard && !places.length && (
            <p className="py-8 text-center text-body-sm text-muted-foreground">
              {search ? 'No rows match' : 'No people yet'}
            </p>
          )}
          <RosterRows places={places} onRemove={(id) => remove.mutate(id)} />
          <p className="mt-2 text-caption text-muted-foreground">
            {dashboard
              ? places.length
                ? `Showing 1–${places.length} of ${places.length}`
                : 'Showing 0 of 0'
              : `${count} ${count === 1 ? 'person' : 'people'}`}
          </p>
        </>
      )}
    </>
  )
}
